import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { ShieldCheck, GitBranch, ClipboardCheck, TrendingUp, ChevronRight, CheckCircle2, Layers, Sparkles, Send } from "lucide-react";
import { toast } from "sonner";

const HEALTH_DOT = { red: "bg-red-500", amber: "bg-amber-500", green: "bg-emerald-500" };
const STATUS_STYLE = {
  calibrated: "bg-emerald-600 text-white",
  calibrated_draft: "bg-emerald-100 text-emerald-800",
  partial: "bg-amber-100 text-amber-800",
  developing: "bg-zinc-200 text-zinc-700",
};

const BandChip = ({ band, index }) => (
  <span className="inline-flex items-center gap-1 text-[12px]">
    <span className="font-semibold">{band || "—"}</span>
    {index != null && <span className="text-zinc-400 tabular-nums">({index})</span>}
  </span>
);

function ExampleDetail({ id }) {
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get(`/public/enterprise360/worked-examples/${id}`).then(({ data }) => setD(data)).catch(() => {}); }, [id]);
  if (!d) return <div className="text-sm text-zinc-400 py-8" data-testid="example-detail-loading">Loading worked example…</div>;
  return <DetailBody d={d} testid={`example-detail-${id}`} />;
}

function DetailBody({ d, testid }) {
  return (
    <div className="space-y-8" data-testid={testid}>
      <div>
        <div className="ek-eyebrow text-[#7737F6]">{d.archetype.name}</div>
        <h3 className="ek-heading text-2xl md:text-3xl mt-2">{d.name}</h3>
        <p className="text-zinc-600 mt-3 max-w-3xl">{d.archetype.summary}</p>
        <p className="text-[12px] text-zinc-400 mt-2 italic">Illustrative worked example — anonymised, generated live by the engine. Not a real client.</p>
      </div>

      {/* Current-state health */}
      <div className="border border-zinc-200 rounded-xl p-5" data-testid="detail-health">
        <div className="flex items-center gap-2 mb-3"><ShieldCheck size={16} className="text-[#7737F6]" /><span className="font-semibold">Current-state diagnosis (Programme Health)</span></div>
        <div className="flex items-center gap-4 text-sm mb-3">
          <span className="inline-flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded-full ${HEALTH_DOT[d.programme_health.overall] || "bg-zinc-400"}`} /> Overall <b className="capitalize">{d.programme_health.overall}</b></span>
          <span className="text-zinc-400">{d.programme_health.reds}R · {d.programme_health.ambers}A · {d.programme_health.greens}G</span>
        </div>
        <div className="grid md:grid-cols-2 gap-2">
          {d.programme_health.signals.map((s, i) => (
            <div key={i} className="flex items-start gap-2 text-[13px]">
              <span className={`mt-1 w-2 h-2 rounded-full shrink-0 ${HEALTH_DOT[s.status] || "bg-zinc-400"}`} />
              <span><b>{s.name}.</b> <span className="text-zinc-600">{s.finding}</span></span>
            </div>
          ))}
        </div>
      </div>

      {/* Value streams: current -> target */}
      <div data-testid="detail-value-streams">
        <div className="flex items-center gap-2 mb-3"><GitBranch size={16} className="text-[#7737F6]" /><span className="font-semibold">Target-state blueprint — {d.value_streams.length} value streams</span></div>
        <div className="overflow-x-auto border border-zinc-200 rounded-xl">
          <table className="w-full text-[13px]">
            <thead><tr className="bg-zinc-50 text-left text-zinc-500">
              <th className="p-3 font-medium">Value stream</th><th className="p-3 font-medium">Current</th>
              <th className="p-3 font-medium">Target</th><th className="p-3 font-medium">Priority move</th></tr></thead>
            <tbody>
              {d.value_streams.map((v, i) => (
                <tr key={i} className="border-t border-zinc-100 align-top">
                  <td className="p-3 font-medium">{v.name}</td>
                  <td className="p-3"><BandChip band={v.current_band} index={v.current_index} /></td>
                  <td className="p-3 text-emerald-700"><BandChip band={v.target_band} index={v.target_index} /></td>
                  <td className="p-3 text-zinc-600">{v.move}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {/* RAID */}
        <div className="border border-zinc-200 rounded-xl p-5" data-testid="detail-raid">
          <div className="flex items-center gap-2 mb-3"><ClipboardCheck size={16} className="text-[#7737F6]" /><span className="font-semibold">Consolidated RAID</span></div>
          <div className="text-[12px] text-zinc-500 mb-3">{Object.entries(d.raid.counts).map(([k, v]) => `${v} ${k}`).join(" · ")}</div>
          <ul className="space-y-2 text-[13px]">
            {d.raid.sample.map((r, i) => (
              <li key={i} className="text-zinc-600"><b className="text-zinc-800">{r.ref} {r.category}:</b> {r.item}</li>
            ))}
          </ul>
        </div>
        {/* Benefits */}
        <div className="border border-zinc-200 rounded-xl p-5" data-testid="detail-benefits">
          <div className="flex items-center gap-2 mb-3"><TrendingUp size={16} className="text-[#7737F6]" /><span className="font-semibold">Benefits — {d.benefits.count} tracked</span></div>
          {d.benefits.value_at_stake_label && <div className="text-sm mb-3">Value at stake: <b>{d.benefits.value_at_stake_label}</b></div>}
          <ul className="space-y-2 text-[13px]">
            {d.benefits.sample.map((b, i) => (
              <li key={i} className="text-zinc-600"><b className="text-zinc-800">{b.benefit}</b> — {b.baseline} → {b.target}</li>
            ))}
          </ul>
        </div>
      </div>

      {/* Assurance */}
      <div className="border border-[#7737F6]/30 bg-[#7737F6]/5 rounded-xl p-5" data-testid="detail-assurance">
        <div className="font-semibold mb-3">System of Proof — how every conclusion is backed</div>
        <div className="flex flex-wrap gap-2">
          {["Evidence graph", "Confidence index", "Hash-chained audit trail", "Human challenge log", "Pre-export QA gate"].map((c) => (
            <span key={c} className="inline-flex items-center gap-1 text-[12px] bg-white border border-[#7737F6]/30 rounded-full px-3 py-1">
              <CheckCircle2 size={12} className="text-[#7737F6]" /> {c}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function CalibrationSection() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/public/enterprise360/calibration-map").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return null;
  return (
    <section className="ek-container py-20" data-testid="public-calibration-map">
      <div className="ek-eyebrow text-[#7737F6]">Honest by design</div>
      <h2 className="ek-heading text-3xl md:text-4xl mt-3 max-w-3xl">Calibrated for your world — and honest about where</h2>
      <p className="text-zinc-600 mt-4 max-w-2xl">
        We publish our calibration rather than over-claim. Each business archetype shows its authored
        value-stream breadth and whether it is already field-validated through live engagements —
        calibration that deepens with every engagement.
      </p>
      <div className="mt-4 flex flex-wrap gap-3 text-sm text-zinc-500">
        <span><b className="text-zinc-800">{d.summary.by_status.calibrated}</b> Calibrated v1</span>
        <span><b className="text-zinc-800">{d.summary.by_status.calibrated_draft}</b> Calibrated (draft)</span>
        <span><b className="text-zinc-800">{d.summary.field_validated}</b> field-validated</span>
      </div>
      <div className="mt-6 overflow-x-auto border border-zinc-200 rounded-xl">
        <table className="w-full text-[13px]" data-testid="public-calibration-table">
          <thead><tr className="bg-zinc-50 text-left text-zinc-500">
            <th className="p-3 font-medium">Archetype</th><th className="p-3 font-medium">Status</th>
            <th className="p-3 font-medium">Confidence</th><th className="p-3 font-medium">Value streams</th>
            <th className="p-3 font-medium">Field-validated</th></tr></thead>
          <tbody>
            {d.rows.map((r, i) => (
              <tr key={i} className="border-t border-zinc-100">
                <td className="p-3 font-medium">{r.name}</td>
                <td className="p-3"><span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_STYLE[r.status]}`}>{r.status_label}</span></td>
                <td className="p-3 text-zinc-600">{r.confidence}</td>
                <td className="p-3 text-zinc-600"><span className="inline-flex items-center gap-1"><Layers size={12} /> {r.value_streams}</span></td>
                <td className="p-3">{r.field_validated ? <CheckCircle2 size={14} className="text-emerald-600" /> : <span className="text-zinc-300">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function InstantPreviewSection() {
  const [dims, setDims] = useState([]);
  const [form, setForm] = useState({ sector: "professional_services", customer_model: "b2b", product_model: "service", ambition: "scale" });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lead, setLead] = useState({ full_name: "", email: "", organisation: "", consent: false });
  const [sent, setSent] = useState(false);

  useEffect(() => { api.get("/public/enterprise360/selector-catalogue").then(({ data }) => setDims(data.dimensions || [])).catch(() => {}); }, []);
  const opts = (id) => (dims.find((x) => x.id === id)?.options) || [];
  const label = (v) => String(v).replace(/_/g, " ");

  const generate = async () => {
    setLoading(true); setPreview(null); setSent(false);
    try {
      const { data } = await api.post("/public/enterprise360/instant-preview", form);
      setPreview(data);
    } catch { toast.error("Could not generate a preview for that profile"); }
    finally { setLoading(false); }
  };
  const submitLead = async () => {
    if (!lead.full_name || !lead.email) { toast.error("Name and email required"); return; }
    if (!lead.consent) { toast.error("Please tick consent to continue"); return; }
    try {
      await api.post("/public/enterprise360/instant-preview/lead", { ...lead, profile: form });
      setSent(true);
      toast.success("Sent — a founder-led follow-up will reach you within one business day.");
    } catch { toast.error("Could not send. Please try again."); }
  };

  const Field = ({ id, testid }) => (
    <label className="flex flex-col gap-1 text-[13px]">
      <span className="text-zinc-500 capitalize">{label(id)}</span>
      <select data-testid={testid} value={form[id]} onChange={(e) => setForm({ ...form, [id]: e.target.value })}
        className="border border-zinc-300 rounded-lg px-3 py-2 bg-white text-zinc-800 min-w-[160px]">
        {opts(id).map((o) => <option key={o} value={o}>{label(o)}</option>)}
      </select>
    </label>
  );

  return (
    <section className="ek-container py-20" data-testid="instant-preview-section">
      <div className="ek-eyebrow text-[#7737F6]">Your turn</div>
      <h2 className="ek-heading text-3xl md:text-4xl mt-3 max-w-3xl">Generate your organisation's version — live</h2>
      <p className="text-zinc-600 mt-4 max-w-2xl">
        Pick your sector and ambition and the engine will build an illustrative blueprint preview for your
        archetype in seconds — current-state diagnosis, target-state and the System of Proof assurance.
      </p>

      <div className="mt-6 flex flex-wrap gap-4 items-end" data-testid="instant-preview-form">
        <Field id="sector" testid="ip-sector" />
        <Field id="customer_model" testid="ip-customer-model" />
        <Field id="product_model" testid="ip-product-model" />
        <Field id="ambition" testid="ip-ambition" />
        <button onClick={generate} disabled={loading} data-testid="ip-generate-btn"
          className="inline-flex items-center gap-2 bg-[#7737F6] text-white font-medium rounded-full px-6 py-2.5 hover:bg-[#6528e0] transition-colors disabled:opacity-60">
          <Sparkles size={16} /> {loading ? "Generating…" : "Generate my preview"}
        </button>
      </div>

      {preview && (
        <div className="mt-10 border border-[#7737F6]/30 rounded-2xl p-6 md:p-8 bg-white" data-testid="ip-preview">
          <DetailBody d={preview} testid="ip-detail-body" />
          {!sent ? (
            <div className="mt-8 border-t border-zinc-200 pt-6" data-testid="ip-lead-form">
              <div className="font-semibold">Get the founder-led read for your organisation</div>
              <p className="text-[13px] text-zinc-500 mt-1 mb-4">We'll follow up within one business day with a focused next step. No slide pitch.</p>
              <div className="flex flex-wrap gap-3 items-center">
                <input data-testid="ip-lead-name" aria-label="Your name" value={lead.full_name} onChange={(e) => setLead({ ...lead, full_name: e.target.value })} placeholder="Your name"
                  className="border border-zinc-300 rounded-lg px-3 py-2 text-[13px]" />
                <input data-testid="ip-lead-email" aria-label="Work email" type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} placeholder="Work email"
                  className="border border-zinc-300 rounded-lg px-3 py-2 text-[13px]" />
                <input data-testid="ip-lead-org" aria-label="Organisation" value={lead.organisation} onChange={(e) => setLead({ ...lead, organisation: e.target.value })} placeholder="Organisation"
                  className="border border-zinc-300 rounded-lg px-3 py-2 text-[13px]" />
                <button onClick={submitLead} data-testid="ip-lead-submit"
                  className="inline-flex items-center gap-2 bg-zinc-900 text-white font-medium rounded-full px-5 py-2 hover:bg-black transition-colors">
                  <Send size={14} /> Send to Ekselens
                </button>
              </div>
              <label className="flex items-start gap-2 text-[12px] text-zinc-500 mt-3 max-w-xl">
                <input type="checkbox" data-testid="ip-lead-consent" checked={lead.consent} onChange={(e) => setLead({ ...lead, consent: e.target.checked })} className="mt-0.5" />
                I'd like Ekselens to follow up. I understand my details will be stored to enable this conversation.
              </label>
            </div>
          ) : (
            <div className="mt-8 border-t border-zinc-200 pt-6 text-emerald-700 font-medium" data-testid="ip-lead-success">
              Thanks — your preview is captured and a founder-led follow-up is on its way.
            </div>
          )}
        </div>
      )}
    </section>
  );
}


export default function SystemOfProofInAction() {
  const [examples, setExamples] = useState([]);
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    api.get("/public/enterprise360/worked-examples").then(({ data }) => {
      setExamples(data.examples || []);
      if (data.examples?.length) setSelected(data.examples[0].id);
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="system-of-proof-in-action-page">
      <section className="bg-[#0B0B0C] text-white">
        <div className="ek-container py-24 md:py-28">
          <div className="ek-eyebrow text-[#A179FA]">System of Proof · in action</div>
          <h1 className="ek-heading text-4xl md:text-5xl lg:text-6xl text-white mt-5 max-w-4xl" data-testid="sopia-headline">
            Seeing is believing.
          </h1>
          <p className="text-zinc-300 font-light text-lg md:text-xl leading-relaxed mt-6 max-w-2xl">
            Real, illustrative worked examples — generated live by the same engine we run on client
            engagements. Watch a current-state diagnosis become an evidence-backed target-state blueprint,
            a mobilisation roadmap and a governed delivery plan.
          </p>
        </div>
      </section>

      <section className="ek-container py-16">
        <div className="grid md:grid-cols-2 gap-4" data-testid="worked-example-cards">
          {examples.map((e) => (
            <button key={e.id} data-testid={`worked-example-card-${e.id}`} onClick={() => setSelected(e.id)}
              className={`text-left border rounded-xl p-5 transition-all ${selected === e.id ? "border-[#7737F6] bg-[#7737F6]/5 shadow-sm" : "border-zinc-200 hover:border-[#7737F6]/50"}`}>
              <div className="ek-eyebrow text-[#7737F6]">{e.archetype}</div>
              <div className="text-lg font-semibold mt-1">{e.name}</div>
              <div className="text-[13px] text-zinc-500 mt-2">{e.value_stream_count} value streams · full evidence-led blueprint</div>
              <div className="inline-flex items-center gap-1 text-[13px] text-[#7737F6] font-medium mt-3">View worked example <ChevronRight size={14} /></div>
            </button>
          ))}
        </div>
        <div className="mt-10">{selected && <ExampleDetail id={selected} />}</div>
      </section>

      <div className="bg-zinc-50 border-y border-zinc-200"><CalibrationSection /></div>

      <InstantPreviewSection />

      <section className="ek-container py-20 text-center">
        <h2 className="ek-heading text-3xl md:text-4xl max-w-2xl mx-auto">See it on your own organisation</h2>
        <p className="text-zinc-600 mt-4 max-w-xl mx-auto">Book a discovery conversation and we will walk your team through an evidence-led diagnosis and target-state blueprint.</p>
        <a href="/contact" data-testid="sopia-cta" className="inline-block mt-6 bg-[#7737F6] text-white font-medium rounded-full px-7 py-3 hover:bg-[#6528e0] transition-colors">Book a discovery conversation</a>
      </section>
    </div>
  );
}
